import React, { useEffect, useState } from "react";
import "./MissionsPage.css";
import { useLanguage } from "../contexts/LanguageContext";
import { useFirebase } from "../contexts/FirebaseContext";
import { isMissionFilled, MISSION_TYPES } from "../constants/missions";
import { getMissionProgressView } from "../services/gameProgress";
import { StarIcon } from "./HomeIcons";

const MissionsPage = () => {
  const { t } = useLanguage();
  const {
    getAllMissions,
    gameProgress,
    missionProgress,
    recentMissionClaims,
    clearRecentMissionClaims,
  } = useFirebase();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [claimBanner, setClaimBanner] = useState("");
  const [openMissionId, setOpenMissionId] = useState(null);

  const loadMissions = async () => {
    setLoading(true);
    setError("");
    try {
      const allMissions = await getAllMissions();
      setMissions(allMissions);
    } catch (err) {
      console.error(err);
      setError(t("missionsLoadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMissions();

    const handleMissionsChanged = () => {
      loadMissions();
    };
    window.addEventListener("arcade-missions-changed", handleMissionsChanged);

    return () => {
      window.removeEventListener(
        "arcade-missions-changed",
        handleMissionsChanged
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!recentMissionClaims?.length) return undefined;
    const total = recentMissionClaims.reduce(
      (sum, item) => sum + (item.rewardPoints || 0),
      0
    );
    const names = recentMissionClaims.map((item) => item.title).join(", ");
    setClaimBanner(
      t("missionCompleteBanner")
        .replace("{points}", String(total))
        .replace("{names}", names)
    );
    const timer = setTimeout(() => {
      setClaimBanner("");
      clearRecentMissionClaims();
    }, 5000);
    return () => clearTimeout(timer);
  }, [recentMissionClaims, clearRecentMissionClaims, t]);

  useEffect(() => {
    if (!openMissionId) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpenMissionId(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [openMissionId]);

  const openMission = missions.find((mission) => mission.id === openMissionId);
  const openFilled = openMission ? isMissionFilled(openMission) : false;
  const openStatus = openMission
    ? getMissionProgressView(
        openMission,
        gameProgress,
        missionProgress?.[openMission.id]
      )
    : null;
  const openShowProgress =
    openMission?.type && openMission.type !== MISSION_TYPES.MANUAL_CONTENT;
  const openPercent =
    openStatus && openStatus.target > 0
      ? Math.min(
          100,
          Math.round((openStatus.progress / openStatus.target) * 100)
        )
      : 0;

  return (
    <div className="sw-missions-page">
      <header className="sw-missions-header">
        <div>
          <p className="sw-missions-eyebrow">{t("missionsEyebrow")}</p>
          <h1 className="sw-missions-heading">{t("missionsTitle")}</h1>
          <p className="sw-missions-sub">{t("missionsSubtitle")}</p>
        </div>
      </header>

      {claimBanner && (
        <div className="sw-missions-claim-banner" role="status">
          {claimBanner}
        </div>
      )}

      {loading && <p className="sw-missions-muted">{t("loading")}</p>}
      {error && <p className="sw-missions-error">{error}</p>}

      {!loading && !error && (
        <div className="sw-missions-gallery">
          {missions.filter(isMissionFilled).map((mission) => {
            const status = getMissionProgressView(
              mission,
              gameProgress,
              missionProgress?.[mission.id]
            );
            const showProgress =
              mission.type && mission.type !== MISSION_TYPES.MANUAL_CONTENT;
            const percent =
              status.target > 0
                ? Math.min(
                    100,
                    Math.round((status.progress / status.target) * 100)
                  )
                : 0;

            return (
              <article
                key={mission.id}
                className={`sw-mission-card is-filled ${
                  status.status === "claimed" ? "is-claimed" : ""
                }`}
                role="button"
                tabIndex={0}
                onClick={() => setOpenMissionId(mission.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setOpenMissionId(mission.id);
                  }
                }}
              >
                {mission.imageUrl ? (
                  <div className="sw-mission-media">
                    <img
                      className="sw-mission-image"
                      src={mission.imageUrl}
                      alt={mission.title || t("missionUntitled")}
                    />
                    {Number(mission.rewardPoints) > 0 && (
                      <span className="sw-mission-reward-badge">
                        +{mission.rewardPoints} {t("stars")}
                      </span>
                    )}
                  </div>
                ) : Number(mission.rewardPoints) > 0 ? (
                  <span className="sw-mission-reward-badge sw-mission-reward-badge--inline">
                    +{mission.rewardPoints} {t("stars")}
                  </span>
                ) : null}
                <div className="sw-mission-body">
                  <h2 className="sw-mission-title">
                    {mission.title?.trim() || t("missionUntitled")}
                  </h2>
                  {mission.text?.trim() ? (
                    <p className="sw-mission-text">{mission.text}</p>
                  ) : null}

                  {showProgress && (
                    <div className="sw-mission-progress">
                      <div className="sw-mission-progress-meta">
                        <span>
                          {status.progress} / {status.target}
                        </span>
                        <span>
                          {status.status === "claimed"
                            ? `+${mission.rewardPoints || 0} ${t("stars")}`
                            : status.complete
                              ? t("missionStatusComplete")
                              : t("missionStatusInProgress")}
                        </span>
                      </div>
                      <div className="sw-mission-progress-track">
                        <div
                          className="sw-mission-progress-fill"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {openFilled && openMission && (
        <div
          className="sw-mission-detail-overlay"
          onClick={() => setOpenMissionId(null)}
        >
          <div
            className="sw-mission-detail"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sw-mission-detail-title"
            onClick={(event) => event.stopPropagation()}
          >
            {openMission.imageUrl ? (
              <div className="sw-mission-detail-media">
                <img
                  className="sw-mission-detail-image"
                  src={openMission.imageUrl}
                  alt=""
                />
              </div>
            ) : null}
            <h2 id="sw-mission-detail-title" className="sw-mission-detail-title">
              {openMission.title?.trim() || t("missionUntitled")}
            </h2>
            <div className="sw-mission-detail-piece">
              <p className="sw-mission-detail-piece-label">
                {t("missionTextLabel")}
              </p>
              <p className="sw-mission-detail-desc">
                {openMission.text?.trim() || t("missionEmptyHint")}
              </p>
            </div>
            <div className="sw-mission-detail-btns">
              {Number(openMission.rewardPoints) > 0 && (
                <div className="sw-mission-detail-btn sw-mission-detail-btn--stars">
                  <StarIcon size={28} solid />
                  <span>
                    +{openMission.rewardPoints} {t("stars")}
                  </span>
                </div>
              )}
              {openShowProgress && openStatus && (
                <div className="sw-mission-detail-btn sw-mission-detail-btn--progress">
                  <span>
                    {openStatus.progress} / {openStatus.target}
                  </span>
                  <span>
                    {openStatus.status === "claimed"
                      ? t("missionStatusClaimed")
                      : openStatus.complete
                        ? t("missionStatusComplete")
                        : t("missionStatusInProgress")}
                  </span>
                  <span
                    className="sw-mission-detail-btn-bar"
                    aria-hidden="true"
                  >
                    <span style={{ width: `${openPercent}%` }} />
                  </span>
                </div>
              )}
            </div>
            <button
              type="button"
              className="sw-mission-detail-close"
              onClick={() => setOpenMissionId(null)}
            >
              {t("missionClose")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MissionsPage;
